import { isErtDebugModeEnabled, getVideoIdentifier } from '../../rrwebUtils'
import { ReplayerControlState, VideoStartEvent, VideoStateChangeEvent } from '../../types'
import { DeferredWorkOperation, PlaceholderWorkQueue } from '../placeholderTracking/PlaceholderWorkQueue'
import { ReplayerCurrentTime, ReplayerProxy } from '../ReplayerProxy'
import { LoggerName, namedLogger } from '../../../../../../utils/logger'

const logger = namedLogger(LoggerName.BaseVideoPlayer)
import { RRWEB_EVENTS, VideoStateChangeTrigger } from '@simplisafe/blueprint-web-dsl'
import { ACCEPTABLE_SLOP_SECONDS, getMostRecentVideoStateEvent, isElementVisible } from './videoPlayerUtils'
import { BaseVideoPlayerListeners } from './BaseVideoPlayerListeners'

export abstract class BaseVideoPlayer extends BaseVideoPlayerListeners {
  // the initial parent is a div placed in DOM body so that we can preload the video and then attach it to the correct place in the DOM when the video start event is reached
  private initialParent: HTMLElement
  // the video shell is a div that contains the placeholder content or the video element itself. This exists so that we can add the vdp warning overlay on top of the video element if needed
  protected videoShell: HTMLElement
  // If the user does not have permission to view the video, this user will only see the video shell that contains a placeholder with an error and this will be undefined.
  private readonly videoStateChangeEvents: VideoStateChangeEvent[]
  public getVideoStateChangeEvents() {
    return this.videoStateChangeEvents
  }
  private lastPlacedInto: HTMLElement | null = null

  protected constructor(
    initialParent: HTMLElement,
    videoShell: HTMLElement,
    videoElement: HTMLVideoElement | undefined,
    videoStartEvent: VideoStartEvent,
    videoStateChangeEvents: VideoStateChangeEvent[]
  ) {
    super(videoElement, videoStartEvent)
    this.initialParent = initialParent
    this.videoShell = videoShell
    this.videoStateChangeEvents = videoStateChangeEvents

    // start the video off in the hidden state
    this.hideAndPauseVideo()

    if (videoElement) {
      videoElement.loop = videoStartEvent.loop
    }
  }

  getVideoPlaceholderName() {
    return this.videoStartEvent.videoPlaceholderName
  }

  isErrorPlaceholder() {
    return false
  }

  getVideoStartEvent() {
    return this.videoStartEvent
  }

  getVideoDetails() {
    return `${this.isErrorPlaceholder() ? 'n/a   ' : this.videoElement?.paused ? 'paused' : 'plying'} - time: ${this.videoElement?.currentTime ?? 0} - ${this.isShown() ? 'shown' : 'hidden'} - ${this.isBuffering() ? 'buffering - ' : ''}${this.getVideoIdentifier()}`
  }

  reAttachToNewInitialParentAfterFullSnapshot(initialParent: HTMLElement) {
    // update initial parent
    this.initialParent = initialParent
    // reAttach
    if (this.videoShell) {
      this.initialParent.appendChild(this.videoShell)
    }

    // the listeners get dropped from the video when it is removed from the dom, so we re-add them as well
    this.reAddEventListeners()

    logger.debug('re-attached', this.getVideoIdentifier(), 'into initialParent', { console: initialParent })
  }

  async showAndResumeAfterFullSnapshot(placeInto: HTMLElement, replayerProxy: ReplayerProxy) {
    logger.debug('showAndResumeAfterFullSnapshot', this.getVideoIdentifier())
    this.reAddEventListeners()

    const replayerCurrentTime = replayerProxy.getReplayerCurrentTime()

    // if it was shown prior to the snapshot, show it again and resume playback
    const isVisible = this.isVisibleAtReplayerCurrentTime(replayerCurrentTime)
    if (isVisible) {
      this.showVideo(placeInto, replayerCurrentTime)

      try {
        const lastVideoStateEvent = getMostRecentVideoStateEvent(this.videoStartEvent, this.videoStateChangeEvents, replayerCurrentTime)
        if (!lastVideoStateEvent?.paused && replayerProxy.isPlaying()) {
          logger.debug('resuming', this.getVideoIdentifier(), '- replayerCurrentTime =', { console: replayerCurrentTime })
          await this.play()
        } else {
          logger.debug('not resuming', this.getVideoIdentifier(), 'because paused or replayer is not playing - replayerCurrentTime =', {
            console: replayerCurrentTime
          })
        }
      } catch (e) {
        logger.error('error resuming video', this.getVideoIdentifier(), e as Error)
      }
    } else {
      this.hideAndPauseVideo()
      logger.debug('not resuming or showing because isVisible == false', this.getVideoIdentifier())
    }
  }

  getVideoIdentifier(): string {
    return getVideoIdentifier(this.videoStartEvent)
  }

  showVideo(placeInto: HTMLElement, replayerCurrentTime: ReplayerCurrentTime, makeLastChild = false): void {
    const { videoShell } = this

    // only place if it is not already placed into the correct element or if we need to make it the last child so that it is on top of other videos
    if (videoShell.parentElement !== placeInto || makeLastChild) {
      placeInto.style.position = 'relative'

      placeInto.appendChild(videoShell)
      this.lastPlacedInto = placeInto
      videoShell.style.position = 'absolute'
      videoShell.style.visibility = 'visible'
      videoShell.style.width = '100%'
      videoShell.style.height = '100%'
      videoShell.style.top = '0'
      videoShell.style.left = '0'

      this.updateBufferingListeners(true)

      logger.debug('showVideo placed', this.getVideoIdentifier(), 'into', { console: placeInto })

      // listen for the placeholder to be removed from dom and automatically hide the video
      this.autoHideVideoWhenPlaceholderNotVisible(placeInto, replayerCurrentTime)
    } else {
      logger.debug('showVideo skipped for', this.getVideoIdentifier())
    }
  }

  private autoHideVideoWhenPlaceholderNotVisible(placeInto: HTMLElement, replayerCurrentTime: ReplayerCurrentTime) {
    if (placeInto.parentElement) {
      const observer = new MutationObserver((mutations, observer) => {
        if (!isElementVisible(placeInto) && !this.isVisibleAtReplayerCurrentTime(replayerCurrentTime)) {
          this.hideAndPauseVideo()
        }
        observer.disconnect()
      })
      observer.observe(placeInto.parentElement, { childList: true, subtree: true })
    }
  }

  /**
   * This hides and pauses the video. You should never hide without pausing, so we have couple these two operations together
   * so that we never make the mistake of not pausing when we hide.
   */
  hideAndPauseVideo(): void {
    logger.debug('hideAndPauseVideo', this.getVideoIdentifier())

    this.pause()

    const { initialParent, videoShell } = this

    if (isErtDebugModeEnabled()) {
      videoShell.style.visibility = 'visible'
      videoShell.style.width = '200px'
      videoShell.style.height = '200px'
    } else {
      videoShell.style.visibility = 'hidden'
      videoShell.style.width = '0px'
      videoShell.style.height = '0px'
    }
    videoShell.style.position = 'relative'
    videoShell.style.top = ''
    videoShell.style.left = ''

    initialParent.appendChild(videoShell)

    this.updateBufferingListeners(false)
  }

  loadedData(readyFn: () => void): void {
    if (this.videoElement) {
      this.videoElement?.addEventListener('loadeddata', readyFn, { once: true })
    } else {
      readyFn()
    }
  }

  /**
   * returns a promise that resolves when the player is able to play video
   */
  async playerReady(): Promise<void> {
    if (this.videoElement) {
      // to make sure we are ready, we await a play call. This will block until enough data is buffered to play the video.
      try {
        logger.debug('playerReady is waiting for a play call to ensure that we are ready', this.getVideoIdentifier())
        this.setMuted(true)
        await this.play()

        // now that we know we can play, we immediately pause and reset the scrubber to the beginning
        this.pause()
        this.videoElement.currentTime = 0
      } catch (e) {
        logger.error('playerReady encountered an error trying to play video', this.getVideoIdentifier(), e as Error)
      } finally {
        this.setMuted(false)
      }
      logger.debug('playerReady is reporting that it is ready to play', this.getVideoIdentifier())
    }
  }

  async play() {
    if (this.videoElement) {
      logger.debug('playing at time:', this.videoElement?.currentTime, this.getVideoIdentifier())
      await this.videoElement.play()
    }
  }

  pause() {
    if (this.videoElement) {
      logger.debug('pausing at time:', this.videoElement?.currentTime, this.getVideoIdentifier())
      this.videoElement.pause()
    }
  }

  setVolume(volume: number) {
    if (this.videoElement) {
      this.videoElement.volume = volume
    }
  }

  setMuted(isMuted: boolean) {
    if (this.videoElement) {
      this.videoElement.muted = isMuted
    }
  }

  setSpeed(speed: number) {
    if (this.videoElement) {
      this.videoElement.playbackRate = speed
    }
  }

  async setFullScreen(fullScreen: boolean) {
    // This is the wrong implementation of replaying fullscreen so we're not using it for now.
    // We'll revisit this eventually, but what we'd want us to simulate fullscreen behavior but
    // within the confines of the ERT player and not trigger the browser's fullscreen API.
    // if (this.videoElement) {
    //   if (fullScreen) {
    //     if (document.fullscreenElement !== this.videoElement) {
    //       return this.videoElement.requestFullscreen()
    //     }
    //   } else {
    //     if (document.fullscreenElement === this.videoElement) {
    //       return document.exitFullscreen()
    //     }
    //   }
    // }
  }

  seekToTimeMs(seekToTimeMs: number) {
    if (this.videoElement) {
      const seekToTimeSeconds = seekToTimeMs / 1000 // convert to seconds
      if (Math.abs(seekToTimeSeconds - this.videoElement.currentTime) > ACCEPTABLE_SLOP_SECONDS) {
        logger.debug(`seekToTimeMs(${seekToTimeMs} ms) for`, this.getVideoIdentifier(), `was ${this.videoElement.currentTime * 1000} ms`)
        this.videoElement.currentTime = seekToTimeSeconds
      } else {
        logger.debug(
          `seekToTimeMs(${seekToTimeMs}) for `,
          this.getVideoIdentifier(),
          'skipped to avoid buffering because it is within five 100ths of second'
        )
      }
    }
  }

  /**
   * This is used to get the video to load up the buffer for current position before the replayer starts playing.
   * @param replayerProxy
   * @param replayerCurrentTime
   */
  async bufferVideoBeforeReplayerStarts(replayerProxy: ReplayerProxy, replayerCurrentTime: ReplayerCurrentTime) {
    logger.debug('syncVideoStateBeforeReplayerStarts - begin', this.getVideoIdentifier())

    // only do this for videos that are visible at the current time
    if (this.isVisibleAtReplayerCurrentTime(replayerCurrentTime)) {
      const lastVideoStateEvent = getMostRecentVideoStateEvent(this.videoStartEvent, this.videoStateChangeEvents, replayerCurrentTime)

      // while prebuffering video, we temporarily need to listeners removed so that we don't trigger buffering events during prebuffering.
      this.detachListeners()

      // this will seek to the appropriate place in the video and play it. This will get the player to queue up the buffer for that spot.
      await this.syncCurrentTimeAndPlayState(lastVideoStateEvent!, replayerProxy, replayerCurrentTime, { skipShowHide: true })

      // make sure we have enough data to play through
      if (this.videoElement?.readyState !== HTMLMediaElement.HAVE_ENOUGH_DATA) {
        await new Promise<void>(resolve => {
          this.videoElement?.addEventListener(
            'canplaythrough',
            () => {
              resolve()
            },
            { once: true }
          )
        })
      }

      // now that we've loaded up the buffer, pause the video so that it is at the correct spot and paused when the replayer starts
      this.pause()

      // prebuffering is done. add back the listeners
      this.reAddEventListeners()

      logger.debug('syncVideoStateBeforeReplayerStarts - end', this.getVideoIdentifier())
    }
  }

  /**
   * This is used to synchronize the video state with the replayer current time. It is used whenever the replayer starts playing.
   * @param placeholderWorkQueue
   * @param replayerControlState
   * @param replayerProxy
   * @param replayerCurrentTime
   * @param options
   */
  syncVideoStateAfterReplayerStarts(
    placeholderWorkQueue: PlaceholderWorkQueue,
    replayerControlState: ReplayerControlState,
    replayerProxy: ReplayerProxy,
    replayerCurrentTime: ReplayerCurrentTime,
    options?: { isSeeking?: boolean }
  ) {
    const { videoStartEvent } = this
    const { videoPlaceholderName } = videoStartEvent
    const showVideo = this.isVisibleAtReplayerCurrentTime(replayerCurrentTime)

    // show or hide the video
    if (showVideo) {
      placeholderWorkQueue.doWorkForVideoPlaceholderWhenReady(videoPlaceholderName, replayerProxy, {
        operation: DeferredWorkOperation.ShowVideo,
        callback: videoPlaceholder => {
          if (videoPlaceholder) {
            logger.debug('syncVideoStateWhenReplayerStarts "should show" ', this.getVideoIdentifier())
            this.showVideo(videoPlaceholder, replayerCurrentTime, true)
          } else {
            logger.debug('syncVideoStateWhenReplayerStarts skipping "should show" because there is no video placeholder', this.getVideoIdentifier())
          }

          const lastVideoStateEvent = getMostRecentVideoStateEvent(this.videoStartEvent, this.videoStateChangeEvents, replayerCurrentTime)

          // we expect lastVideoStateEvent to be defined because it can't be visible unless there is a start event that makes it visible, and we shouldn't be calling this function unless the video is visible.
          this.applyVideoStateEvent(lastVideoStateEvent!, replayerProxy, replayerCurrentTime, replayerControlState, {
            isSyncingVideoState: true,
            ...options
          })
        }
      })
    } else {
      logger.debug('syncVideoStateWhenReplayerStarts "should hide"', this.getVideoIdentifier())
      this.hideAndPauseVideo()
    }
  }

  /**
   * The flv player adjusts the speed during live playback, but when we play back here we need to keep it pinned to 1
   * @param lastVideoStateEvent
   * @private
   */
  private getSpeedAdjustedForFlvPlayer(lastVideoStateEvent: VideoStartEvent | VideoStateChangeEvent) {
    const { speed: recordedSpeed, isLive, recordingType } = lastVideoStateEvent
    return isLive && recordingType === 'flv' ? 1 : recordedSpeed
  }

  /**
   * This function applies the given video state event to the video element.
   * @param lastVideoStateEvent
   * @param replayerProxy
   * @param replayerCurrentTime
   * @param replayerControlState
   * @param options
   */
  public async applyVideoStateEvent(
    lastVideoStateEvent: VideoStartEvent | VideoStateChangeEvent,
    replayerProxy: ReplayerProxy,
    replayerCurrentTime: ReplayerCurrentTime,
    replayerControlState: ReplayerControlState,
    options?: { isSyncingVideoState?: boolean; skipShowHide?: boolean }
  ) {
    const { videoElement } = this

    if (videoElement) {
      const { loop, volume, muted, fullScreen } = lastVideoStateEvent

      const speed = this.getSpeedAdjustedForFlvPlayer(lastVideoStateEvent)

      const trigger: VideoStateChangeTrigger | undefined = (lastVideoStateEvent as VideoStateChangeEvent).trigger

      const observedVolume = volume === undefined ? 1 : volume
      this.setVolume(observedVolume * replayerControlState.volume)

      const observedMuted = muted === undefined ? false : muted
      this.setMuted(observedMuted || replayerControlState.muted)

      const observedSpeed = speed === undefined ? 1 : speed
      const newSpeed = observedSpeed * replayerControlState.speed

      this.setSpeed(newSpeed)
      // if the video hasn't loaded data yet, then the speed won't stick, so we set it again after it has loaded
      this.loadedData(() => {
        this.setSpeed(newSpeed)
      })
      videoElement.loop = loop

      if (!options?.skipShowHide) {
        logger.debug('Setting full screen state:', fullScreen, { console: options })
        await this.setFullScreen(fullScreen)
      }

      if (
        options?.isSyncingVideoState ||
        lastVideoStateEvent.eventName === RRWEB_EVENTS.VIDEO_START ||
        [VideoStateChangeTrigger.Seek, VideoStateChangeTrigger.Play, VideoStateChangeTrigger.Pause, VideoStateChangeTrigger.SpeedChange].includes(
          trigger
        )
      ) {
        await this.syncCurrentTimeAndPlayState(lastVideoStateEvent, replayerProxy, replayerCurrentTime, options)
      }
    } else {
      logger.debug('applyVideoStateEvent no videoElement for', this.getVideoIdentifier())
    }
  }

  private async syncCurrentTimeAndPlayState(
    lastVideoStateEvent: VideoStartEvent | VideoStateChangeEvent,
    replayerProxy: ReplayerProxy,
    replayerCurrentTime: ReplayerCurrentTime,
    options?: { skipShowHide?: boolean }
  ) {
    const {
      startTimeEpoch,
      endTimeEpoch,
      paused,
      eventTime,
      adjustedCurrentTimeSeconds,
      adjustmentForCurrentTimeSeconds,
      isLive,
      recordingDetails,
      recordingType
    } = lastVideoStateEvent

    if (adjustedCurrentTimeSeconds === undefined || adjustmentForCurrentTimeSeconds === undefined) {
      throw new Error('adjustedCurrentTimeSeconds is undefined! There is likely a bug in the ErtPreprocessor.')
    }

    const speed = this.getSpeedAdjustedForFlvPlayer(lastVideoStateEvent)

    let videoLengthMs: number
    if (isLive) {
      videoLengthMs = (endTimeEpoch ?? startTimeEpoch) - startTimeEpoch
      if (videoLengthMs === 0) {
        // Video length resolved to 0, so this clip can't be synced/played correctly - error.
        logger.error(
          `videoLengthMs is 0 for ${this.getVideoIdentifier()}. This is likely a bug in the instrumentation.`,
          recordingDetails?.durationMillis
        )
        return
      }
    } else {
      if (!recordingDetails?.durationMillis) {
        // Missing recording duration, so this clip can't be synced/played correctly - error.
        logger.error(
          `recordingDetails?.durationMillis is not defined for ${this.getVideoIdentifier()}. This is likely a bug in the instrumentation.`,
          recordingDetails?.durationMillis
        )
        return
      }
      videoLengthMs = recordingDetails?.durationMillis
    }

    let seekToTimeMs: number

    // for live flv videos, we have to ignore the adjustedCurrentTimeSeconds and set the current time strictly based on the time elapsed since start
    if (recordingType === 'flv' && isLive) {
      const millisSinceVideoStart = replayerCurrentTime.epoch - startTimeEpoch
      seekToTimeMs = millisSinceVideoStart + adjustmentForCurrentTimeSeconds * 1000
    } else {
      // this is the number of millis from the beginning of the recording until the video state event was emitted
      const relativeEventTimeMs = eventTime - replayerProxy.getRecordingStartTimeEpoch()
      // this is the number of millis from when the video state event was emitted until the replayer current time
      const timeElapsedSinceLatestVideoStateEventMs = replayerCurrentTime.relative - relativeEventTimeMs
      // this is the current time of the video at the moment the video state event was emitted
      const videoCurrentTimeAtLastStateEventMs = adjustedCurrentTimeSeconds * 1000 // convert to milliseconds
      // this is the amount of video time that would have been covered since the video state event was emitted, taking into account speed
      // rounding because we are now fully converted to ms and should be using whole numbers
      const videoTimeCoveredSinceLatestVideoStateEventMs = Math.round(timeElapsedSinceLatestVideoStateEventMs * speed)
      // this is the time we need to seek to in the video represented in milliseconds
      seekToTimeMs = Math.max(0, (videoCurrentTimeAtLastStateEventMs + videoTimeCoveredSinceLatestVideoStateEventMs) % videoLengthMs)
    }

    this.seekToTimeMs(seekToTimeMs)

    if (!paused && replayerProxy.isPlaying()) {
      if (this.lastPlacedInto && !options?.skipShowHide) {
        /* Call show again whenever we are resuming. This is because there may be cases where two videos are trying to play in the same 
        placeholder and this will ensure the right one is on top. */
        logger.debug('applyVideoStateEvent placing video back on top', this.getVideoIdentifier(), 'seekToTimeMs', seekToTimeMs)
        this.showVideo(this.lastPlacedInto, replayerCurrentTime, true)
      }

      logger.debug('applyVideoStateEvent attempting to play video', this.getVideoIdentifier(), 'seekToTimeMs', seekToTimeMs)
      try {
        await this.play()
        logger.debug('applyVideoStateEvent completed playing video', this.getVideoIdentifier(), 'seekToTimeMs', seekToTimeMs)
      } catch (e) {
        logger.error(
          'applyVideoStateEvent encountered error trying to play video',
          this.getVideoIdentifier(),
          'seekToTimeMs',
          seekToTimeMs,
          e as Error
        )
      }
    }
    if (paused) {
      this.pause()
      logger.debug('applyVideoStateEvent pausing video', this.getVideoIdentifier(), 'seekToTimeMs', seekToTimeMs)
    }
  }

  public isVisibleAtReplayerCurrentTime(replayerCurrentTime: ReplayerCurrentTime) {
    const { startTimeEpoch, loop } = this.videoStartEvent
    const endTimeEpoch = loop ? Number.MAX_SAFE_INTEGER : (this.videoStartEvent.endTimeEpoch ?? Number.MAX_SAFE_INTEGER)
    const replayerCurrentTimeEpoch = replayerCurrentTime.epoch
    return startTimeEpoch <= replayerCurrentTimeEpoch && replayerCurrentTimeEpoch <= endTimeEpoch
  }
}
